import React, { useState, useRef, useCallback } from 'react';
import { FileSearch, Upload, AlertTriangle, AlertCircle, CheckCircle, Loader, X, RotateCcw, FileText } from 'lucide-react';
import { Card, PageHeader } from '../components/Card';
import { apiUrl } from '../utils/api';
import { auth } from '../firebase';

const ACCEPTED_TYPES = [
  'application/pdf',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.docx'];

function canReview(file) {
  return ACCEPTED_TYPES.includes(file.type) ||
    ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
}

function DisclaimerBanner() {
  return (
    <div style={{
      background: '#fffbeb',
      border: '1px solid #f59e0b',
      borderRadius: 'var(--radius-sm)',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      marginBottom: 20,
    }}>
      <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
      <p style={{ fontSize: 13, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
        <strong>Important:</strong> This AI review is for informational purposes only and does not constitute legal advice.
        Always consult with your realtor or real estate lawyer before making any serious financial decisions.
      </p>
    </div>
  );
}

function ReviewSection({ title, color, icon: Icon, items, emptyMessage }) {
  if (!items || items.length === 0) {
    return (
      <Card style={{ marginBottom: 16, borderLeft: `4px solid ${color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Icon size={20} color={color} />
          <h3 style={{ fontSize: 16, fontWeight: 700, color, margin: 0 }}>{title}</h3>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-400)', margin: 0, fontStyle: 'italic' }}>
          {emptyMessage}
        </p>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 16, borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Icon size={20} color={color} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color, margin: 0 }}>
          {title} <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--gray-500)' }}>({items.length})</span>
        </h3>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {items.map((item, i) => (
          <li key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 0',
            borderBottom: i < items.length - 1 ? '1px solid var(--gray-100)' : 'none',
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: color, flexShrink: 0, marginTop: 7,
            }} />
            <span style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--dark)' }}>{item}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function ContractReview() {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!canReview(f)) {
      setError('Please upload a PDF, Word document (.docx), or plain text file (.txt)');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File size must be under 10 MB');
      return;
    }
    setFile(f);
    setReview(null);
    setError(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleAnalyze = async () => {
    if (!file || isAnalyzing) return;
    setIsAnalyzing(true);
    setError(null);

    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(apiUrl('/api/contract-review'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileData: dataUrl,
          fileType: file.type || 'text/plain',
          fileName: file.name,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      setReview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setReview(null);
    setError(null);
  };

  return (
    <div>
      <PageHeader
        icon={<FileSearch size={22} />}
        title="Contract Review"
        subtitle="Upload a contract for an instant AI-powered plain-English analysis."
      />

      <DisclaimerBanner />

      {!review ? (
        <Card>
          {!file ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? 'var(--primary)' : 'var(--gray-300)'}`,
                borderRadius: 'var(--radius)',
                background: isDragging ? 'var(--primary-light)' : 'var(--gray-50)',
                padding: '48px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <Upload size={40} color={isDragging ? 'var(--primary)' : 'var(--gray-300)'} style={{ marginBottom: 16 }} />
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark)', marginBottom: 8 }}>
                Drop your contract here
              </h3>
              <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 4 }}>
                or click to browse files
              </p>
              <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                Supports PDF, Word (.docx), and plain text — up to 10 MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.docx"
                onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px',
                background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)',
                marginBottom: 20, border: '1px solid var(--gray-200)',
              }}>
                <FileText size={20} color="#dc3545" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  style={{ background: 'none', color: 'var(--gray-400)', padding: 4 }}
                  title="Remove file"
                >
                  <X size={18} />
                </button>
              </div>

              {error && (
                <div style={{
                  padding: '10px 14px', background: '#fef2f2',
                  border: '1px solid #fecaca', borderRadius: 'var(--radius-sm)',
                  fontSize: 13, color: '#dc2626', marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: isAnalyzing ? 'var(--gray-300)' : 'var(--primary)',
                  color: 'white',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 600, fontSize: 15,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                }}
              >
                {isAnalyzing ? (
                  <>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Analyzing contract... this may take 15–30 seconds
                  </>
                ) : (
                  <>
                    <FileSearch size={18} />
                    Analyze Contract
                  </>
                )}
              </button>
            </div>
          )}
        </Card>
      ) : (
        <div>
          <Card style={{ marginBottom: 16, background: 'var(--gray-50)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  {review.documentType || 'Contract'}
                </div>
                <p style={{ fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.6, margin: 0 }}>
                  {review.summary}
                </p>
              </div>
              <button
                onClick={handleReset}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--gray-300)', background: 'white',
                  fontSize: 13, color: 'var(--gray-600)', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <RotateCcw size={14} /> Review Another
              </button>
            </div>
          </Card>

          <ReviewSection
            title="Red Flags"
            color="#dc3545"
            icon={AlertCircle}
            items={review.redFlags}
            emptyMessage="No serious red flags found — but still review with your lawyer."
          />

          <ReviewSection
            title="Cautions"
            color="#e8972c"
            icon={AlertTriangle}
            items={review.cautions}
            emptyMessage="No notable cautions identified."
          />

          <ReviewSection
            title="Excellent"
            color="#2d8a4e"
            icon={CheckCircle}
            items={review.positives}
            emptyMessage="No buyer-friendly highlights identified."
          />

          <DisclaimerBanner />

          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              onClick={handleReset}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 'var(--radius-sm)',
                background: 'var(--primary)', color: 'white',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              <RotateCcw size={16} /> Review Another Contract
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
